import { apiReq } from "./_req";
import type { GetNotificationsResponse } from "@backend-ctrl/notification.controller";
import { useMutation, useQuery, UseQueryOptions } from "react-query";
import { CacheKeyTypes } from "../cache";
import queryClient from "@services/query-client";
import trpcClient, { UserState } from "../rpc-client";
import { storageKeys } from "@src/constants";
import { deleteCookie, getPushSub, setCookie } from "@src/utils";
// import { NOTIFICATIONS_STALETIME } from "../../constants";

const base = `/notifications`;

const getNotifications = async () => {
  return (await apiReq.get<GetNotificationsResponse>(base)).data;
};
export const useNotifications = (opts?: UseQueryOptions<GetNotificationsResponse, Error>) => {
  const queryKey = CacheKeyTypes.Notifications;
  const q = useQuery<GetNotificationsResponse, Error>(queryKey, () => getNotifications(), {
    refetchOnWindowFocus: false,
    ...opts,
  });
  return q;
};

type IVars_updateNotification = { id: string; hidden?: boolean };
/** TODO: vars not being used rn */
const updateNotification = async ({ id, hidden }: IVars_updateNotification) => {
  const body = { hidden };
  return apiReq.post(`/notification/${id}/hide`, body);
};
export const useUpdateNotification = () => {
  type Context = { previousNotifications: GetNotificationsResponse };
  const mutation = useMutation<Await<ReturnType<typeof updateNotification>>, Error, IVars_updateNotification, Context>(
    // (vars) => updateNotification(vars),
    {
      mutationFn: (vars) => updateNotification(vars),
      retry: false,
      onMutate: (vars) => {
        // await queryClient.cancelQueries(CacheKeyTypes.Notifications)
        const previousNotifications = queryClient.getQueryData<GetNotificationsResponse>(CacheKeyTypes.Notifications);
        if (!previousNotifications) return;

        const notifications = previousNotifications.filter((notification) => notification.id !== vars.id);
        queryClient.setQueryData<GetNotificationsResponse>(CacheKeyTypes.Notifications, notifications);

        return { previousNotifications };
      },
      onError: (err, vars, context) => {
        alert(`Error: ${err?.message}`); // TODO: error display
        if (context?.previousNotifications) {
          queryClient.setQueryData<GetNotificationsResponse>(CacheKeyTypes.Notifications, context.previousNotifications);
        }
      },
      // onSuccess: (r, vars) => { queryClient.invalidateQueries(CacheKeyTypes.Notifications) },
    }
  );

  return mutation;
};

export const notificationsSeen = async () => {
  const userState = queryClient.getQueryData<UserState>(CacheKeyTypes.UserState);
  if (userState) {
    userState.notification_unseen_count = 0;
    userState.announcement_unseen_count = 0;
    queryClient.setQueryData<UserState>(CacheKeyTypes.UserState, userState);
  }
  const res = await apiReq.post(`${base}/seen`);
  return res;
};

export const registerPushNotifications = async (send_test = false) => {
  const status = Notification.permission === "granted" ? "granted" : await Notification.requestPermission();
  if (status === "default" || status === "denied") throw new Error(`User did not allow Notification permission.`);

  const registration = await navigator.serviceWorker.getRegistration();
  if (!registration) throw new Error(`Service Worker is not registered.`);

  const _sub = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: import.meta.env.VITE_VPUB,
  });
  if (!_sub) throw new Error("Subscribe response was empty.");

  const { endpoint, keys, expirationTime } = _sub.toJSON();
  if (!endpoint || !keys || !keys.auth || !keys.p256dh) throw new Error(`Failed to obtain required keys.`);

  const res = await trpcClient.mutation("me/push/register", {
    auth: keys.auth,
    endpoint: endpoint,
    p256dh: keys.p256dh,
    expiration_time: expirationTime?.toString() || null,
    send_test,
    // TODO: don't use httponly cookie and just have it in one place
    sub_token: getPushSub() || undefined,
  });
  if (getPushSub() !== res.sub_token) {
    setCookie(storageKeys.pushsub, res.sub_token, 365);
  }
  localStorage.setItem(storageKeys.pushsubrefreshed, Date.now().toString());
  // invalidateKeysByType([CacheKeyTypes.Settings]);

  return res;
};

export const unregisterPusNotifications = async () => {
  const sub_token = getPushSub();
  if (!sub_token) throw new Error(`Token is not stored.`);
  const res = await trpcClient.mutation("me/push/unregister", { sub_token });
  deleteCookie(storageKeys.pushsub, "", "");
  // invalidateKeysByType([CacheKeyTypes.Settings]);
  return res;
};
